// History-API router. Pages are functions of the matched params and the query string.

export type Params = Record<string, string>;
export type Page = (params: Params, query: URLSearchParams) => HTMLElement | Promise<HTMLElement>;

interface Route {
  pattern: string;
  re: RegExp;
  keys: string[];
  page: Page;
}

const routes: Route[] = [];

export function route(pattern: string, page: Page): void {
  const keys: string[] = [];
  const re = new RegExp(
    "^" +
      pattern.replace(/\/:([a-zA-Z]+)/g, (_, k: string) => {
        keys.push(k);
        return "/([^/]+)";
      }) +
      "/?$",
  );
  routes.push({ pattern, re, keys, page });
}

export function match(path: string): { page: Page; params: Params } | null {
  for (const r of routes) {
    const m = r.re.exec(path);
    if (!m) continue;
    const params: Params = {};
    r.keys.forEach((k, i) => {
      try {
        params[k] = decodeURIComponent(m[i + 1]!);
      } catch {
        params[k] = m[i + 1]!;
      }
    });
    return { page: r.page, params };
  }
  return null;
}

let render: (() => Promise<void>) | null = null;

export function navigate(href: string, replace = false): void {
  if (replace) history.replaceState(null, "", href);
  else history.pushState(null, "", href);
  void render?.();
}

/** `before` runs ahead of every render (the site reads money's `selling` there, cached for a minute). */
export function startRouter(outlet: HTMLElement, notFound: Page, after?: () => void, before?: () => Promise<unknown>): void {
  let seq = 0;
  render = async () => {
    const mine = ++seq;
    await before?.();
    if (mine !== seq) return;
    const m = match(location.pathname);
    const query = new URLSearchParams(location.search);
    let el: HTMLElement;
    try {
      el = await (m ? m.page(m.params, query) : notFound({}, query));
    } catch (e) {
      el = await notFound({ error: e instanceof Error ? e.message : String(e) }, query);
    }
    if (mine !== seq) return; // a later navigation won
    outlet.replaceChildren(el);
    if (!location.hash) window.scrollTo(0, 0);
    after?.();
  };
  document.addEventListener("click", (e) => {
    const a = (e.target as Element | null)?.closest?.("a[data-link]") as HTMLAnchorElement | null;
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  });
  window.addEventListener("popstate", () => void render?.());
  void render();
}

/** Re-render the current page (after sign-in, for example). */
export function rerender(): void {
  void render?.();
}

export function setTitle(title: string | null): void {
  document.title = title ? `${title} · Banger` : "Banger";
}
